import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Editor from '@monaco-editor/react';
import { toast } from 'react-toastify';

import {
  Box,
  Button,
  TextField,
  Typography
} from '@mui/material';

import LoadingButton from '../../../../components/buttons/Button';
import TopBar from '../../../../components/utils/TopBar';

import {
  GetEmailTemplateBIZnotifications
} from '../../../../redux/actions/business/notifications/EmailTemplatesActions';

import style from "../../../../global.scss";
import { SendTestEmailBIZnotifications } from '../../../../redux/actions/business/notifications/EmailTesterActions';

const Data = {
  templateId: "",
  htmlTemplate: "",
  jsonPayload: "{}",
  subject: "",
  email: ""
};

const EmailTesterPage = () => {
  const dispatch = useDispatch();
  const state = useSelector(state => state["business.notifications"]);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [formData, setFormData] = useState(Data);
  const htmlEditorRef = useRef(null);
  const jsonEditorRef = useRef(null);

  useEffect(() => {
    try {
      if(state.emailTemplateDetails && state.emailTemplateDetails.id) {
        setFormData({
          ...formData,
          htmlTemplate: state.emailTemplateDetails.template || ""
        });
      }
    } catch (err){
      console.log(err);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  function handleHtmlEditorDidMount(editor, monaco) {
    htmlEditorRef.current = editor;
    editor.getAction('editor.action.formatDocument').run();
  }

  function handleJsonEditorDidMount(editor, monaco) {
    jsonEditorRef.current = editor;
    editor.getAction('editor.action.formatDocument').run();
  }

  const handleGetTemplate = () => {
    if(!formData.templateId || formData.templateId.trim() === "") {
      toast.error("Please enter a template ID");
      return;
    }
    dispatch(GetEmailTemplateBIZnotifications(formData.templateId, setLoadingTemplate));
  };

  const handleReset = () => {
    setFormData(Data);
  };

  const handleSendEmail = async () => {
    if(!formData.email || formData.email.trim() === "") {
      toast.error("Please enter an email address");
      return;
    }
    if(!formData.htmlTemplate || formData.htmlTemplate.trim() === "") {
      toast.error("Please load a template or enter HTML");
      return;
    }

    // Validate JSON payload
    try {
      JSON.parse(formData.jsonPayload);
    } catch (err) {
      toast.error("Invalid JSON payload");
      return;
    }

    let obj = {
      template: formData.htmlTemplate,
      data: JSON.parse(formData.jsonPayload),
      subject: formData.subject,
      to: formData.email
    };

    dispatch(SendTestEmailBIZnotifications(obj, setSendingEmail));
  };

  return (
    <Box>
      <TopBar
        title={"Email Tester"}
        />

      <Box>
        
        {/* Template ID Search Section */}
        <Box className="block" sx={{ padding: "20px", marginBottom: "15px" }}>
          <Box 
            className="flexCenter_Row"
            gap={"15px"}>
            <TextField
              fullWidth
              label="Template ID"
              name="templateId"
              value={formData.templateId}
              onChange={(e) => setFormData({ ...formData, templateId: e.target.value })}
              disabled={loadingTemplate}
              placeholder="Enter template ID to fetch template"
            />
            <LoadingButton
              variant='contained'
              loading={loadingTemplate}
              onClick={handleGetTemplate}
              sx={{ minWidth: "120px" }}>
              Search
            </LoadingButton>
          </Box>
        </Box>

        {/* HTML Template Editor and Preview Section */}
        <Box className="block" sx={{ padding: "20px", marginBottom: "15px" }}>
          <Box 
            className="flexCenter_Row"
            gap={"15px"}
            sx={{ alignItems: "flex-start" }}>
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
              <Typography variant='subtitle2' mb={"8px"} sx={{ color: style["primary"] }}>
                Template (HTML)
              </Typography>
              <Editor
                height="450px"
                language={"html"}
                value={formData.htmlTemplate || "<!-- Enter HTML template here -->"}
                onChange={(value) => setFormData({ ...formData, htmlTemplate: value })}
                theme='vs-dark'
                options={{
                  wordWrap: true,
                  formatOnPaste: true,
                  formatOnType: true,
                  scrollBeyondLastLine: false,
                  minimap: { enabled: false }
                }}
                onMount={handleHtmlEditorDidMount}
              />
            </Box>
            
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
              <Typography variant='subtitle2' mb={"8px"} sx={{ color: style["primary"] }}>
                Email Preview
              </Typography>
              <Box
                sx={{
                  height: "450px",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  borderRadius: "4px",
                  overflow: "auto",
                  backgroundColor: "#ffffff"
                }}>
                <iframe
                  title="Email Preview"
                  srcDoc={`
                    <!DOCTYPE html>
                    <html>
                      <head>
                        <meta charset="utf-8">
                        <meta name="viewport" content="width=device-width, initial-scale=1.0">
                        <style>
                          body {
                            margin: 0;
                            padding: 16px;
                            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif;
                            font-size: 14px;
                            line-height: 1.6;
                            color: #000000;
                            background-color: #ffffff;
                          }
                          * {
                            box-sizing: border-box;
                          }
                        </style>
                      </head>
                      <body>
                        ${formData.htmlTemplate || '<p style="color: #666; font-style: italic;">Your email preview will appear here...</p>'}
                      </body>
                    </html>
                  `}
                  style={{
                    width: "100%",
                    height: "100%",
                    border: "none"
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Box>

        {/* JSON Payload and Send Section */}
        <Box className="block" sx={{ padding: "20px" }}>
          <Typography variant='subtitle2' mb={"8px"} sx={{ color: style["primary"] }}>
            Payload (JSON)
          </Typography>
          <Box mb={"15px"}>
            <Editor
              height="150px"
              language={"json"}
              value={formData.jsonPayload}
              onChange={(value) => setFormData({ ...formData, jsonPayload: value })}
              theme='vs-dark'
              options={{
                wordWrap: true,
                formatOnPaste: true,
                formatOnType: true,
                scrollBeyondLastLine: false,
                minimap: { enabled: false }
              }}
              onMount={handleJsonEditorDidMount}
            />
          </Box>

          <Box 
            className="flexCenter_Row"
            gap={"15px"}
            mb={"15px"}>
            <TextField
              fullWidth
              label="Subject"
              name="subject"
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              disabled={sendingEmail}
              placeholder="Enter email subject (optional)"
            />
          </Box>

          <Box 
            className="flexCenter_Row"
            gap={"15px"}
            mb={"15px"}>
            <TextField
              fullWidth
              label="Recipient Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              disabled={sendingEmail}
              placeholder="Enter recipient email address"
            />
          </Box>

          <Box className="flexCenterFERow" sx={{ gap: "15px" }}>
            <Button 
              variant='outlined'
              onClick={handleReset}
              disabled={sendingEmail}>
              Reset
            </Button>
            <LoadingButton
              variant='contained'
              loading={sendingEmail}
              disabled={
                !(formData?.email?.trim() !== "" && formData?.htmlTemplate?.trim() !== "")
              }
              onClick={handleSendEmail}>
              Send Email
            </LoadingButton>
          </Box>
        </Box>

      </Box>
    </Box>
  );
}

export default EmailTesterPage;